import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../../services/userSettings.service';
import {
  DrillCardState,
  cardsFromLayoutResponse,
  cardsToLayoutRequest,
  mergeLoadedCards,
  normalizeExclusive,
} from './drillCardLayout';

const RETRY_MS = 15000;
const SAVE_DEBOUNCE_MS = 500;

/**
 * Which drill is open (exclusive: at most one `visible` card, like LC Dashboards). Saving is
 * gated on a successful load for the same reason as the tile layout: a failed load leaves an
 * empty list, and saving that would soft-delete every card the user had.
 *
 * @param dashboardType Should be stable for the hook's lifetime. The save gate also checks the
 *   loaded type, so a change can never save the previous dashboard's cards under the new type
 *   (the server's orphan sweep would then delete the new dashboard's other cards).
 */
export function useDrillCardLayout(
  dashboardType: string,
  /** Called when a debounced save rejects, so the page can toast; state is kept and the next change retries. */
  onSaveError?: (err: unknown) => void,
) {
  const [cards, setCardsState] = useState<DrillCardState[]>([]);
  const cardsRef = useRef<DrillCardState[]>(cards);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // The dashboardType the current `cards` were loaded for; the save gate checks it.
  const loadedForRef = useRef<string | null>(null);
  // Ref so an inline arrow from the page never re-arms the debounce effect.
  const onSaveErrorRef = useRef(onSaveError);
  onSaveErrorRef.current = onSaveError;

  const setCards = useCallback((next: DrillCardState[]) => {
    cardsRef.current = next;
    setCardsState(next);
  }, []);

  useEffect(() => {
    // A different dashboard than the one we loaded: drop its cards before loading so the
    // merge cannot carry them across, and re-block saving until the new load lands.
    if (loadedForRef.current !== null && loadedForRef.current !== dashboardType) {
      loadedForRef.current = null;
      setCards([]);
      setLoaded(false);
    }
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await userSettingsService.getDashboardLayoutNew(dashboardType);
        if (cancelled) return;
        // One open drill, even if the saved rows or the merge say otherwise; the card the
        // user opened while the load was failing wins over the server's stale open card.
        const localOpen = cardsRef.current.find((c) => c.visible)?.key ?? null;
        setCards(normalizeExclusive(mergeLoadedCards(cardsFromLayoutResponse(resp), cardsRef.current), localOpen));
        setLoadFailed(false);
        loadedForRef.current = dashboardType;
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load drill card layout — card saving stays blocked:', err);
        setLoadFailed(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dashboardType, loadAttempt, setCards]);

  // A failed load must never unblock saving — an empty save soft-deletes every card row
  // server-side (_do_save_layout's orphan sweep) — so retry instead.
  useEffect(() => {
    if (!loadFailed) return;
    const t = setTimeout(() => setLoadAttempt((a) => a + 1), RETRY_MS);
    return () => clearTimeout(t);
  }, [loadFailed, loadAttempt]);

  // A successful load triggers one save of the just-loaded state ~500ms later. Intentional: it
  // creates the workspace row for a new user and is a harmless round-trip for an existing one.
  // A pending save is dropped on unmount (navigate away within 500ms of a change loses it) —
  // same as LC; not worth a flush.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!loaded || loadedForRef.current !== dashboardType) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      userSettingsService.saveDashboardLayoutNew(dashboardType, cardsToLayoutRequest(cards))
        .catch((err) => {
          console.error('Failed to save drill card layout:', err);
          onSaveErrorRef.current?.(err);
        });
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [cards, loaded, dashboardType]);

  /**
   * Exclusive selection, like LC's toggleCardVisibility: re-selecting the one open card
   * closes it (returns false); otherwise `key` becomes the only visible card (created on
   * first use) and every other card is hidden (returns true).
   */
  const selectCard = useCallback((key: string, title: string): boolean => {
    const current = cardsRef.current;
    const visible = current.filter((c) => c.visible);
    if (visible.length === 1 && visible[0].key === key) {
      setCards(current.map((c) => (c.key === key ? { ...c, visible: false } : c)));
      return false;
    }
    const known = current.some((c) => c.key === key);
    const next = current.map((c) => ({ ...c, visible: c.key === key }));
    setCards(known ? next : [...next, { key, title, visible: true }]);
    return true;
  }, [setCards]);

  const hideCard = useCallback((key: string) => {
    setCards(cardsRef.current.map((c) => (c.key === key ? { ...c, visible: false } : c)));
  }, [setCards]);

  return { cards, loaded, loadFailed, selectCard, hideCard };
}
